/**
 * ai-library-page.tsx — Nhân viên AI › "Thư mục dùng chung" (`/ai/thu-vien`), tầng TỔ CHỨC:
 *  - Quy tắc an toàn chung (tài liệu index / mechanism / criteria / handoff_rules) — áp cho mọi nhân viên AI trừ nhân viên đã tắt;
 *  - Kỹ năng — mỗi kỹ năng có "Áp dụng cho" (Tất cả / các nhân viên chọn); kỹ năng mới mặc định chưa áp ai;
 *  - Mẫu nhân vật / quy trình bán hàng (persona / playbook) — để chép vào nhân viên AI, không tự áp dụng.
 * Dữ liệu: GET /ai/library (dự phòng API cũ — xem use-ai-khung.ts).
 */
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { BookCopy, Pencil, Plus, ShieldCheck, Wand2 } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/misc'
import { apiError } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { updateScenario, useAiBots, type ScenarioMeta } from '@/hooks/use-ai'
import { invalidateLibrary, LIBRARY_DOC_LABEL, TEMPLATE_TYPES, useAiLibrary, type LibraryDoc } from '@/hooks/use-ai-khung'
import { skillScopeLabel } from '@/lib/ai-khung'
import { useQueryClient } from '@tanstack/react-query'
import { LibraryDocDialog, ScopeDialog, SkillDialog } from './library/library-dialogs'
import { useAiPerms } from './use-ai-perms'

/** Dòng đầu có chữ của tài liệu (xem nhanh). */
function preview(content: string | null): string {
  const line = (content ?? '').split('\n').map((l) => l.replace(/^[#>*\-\s]+/, '').trim()).find(Boolean)
  return line ? (line.length > 140 ? `${line.slice(0, 140)}…` : line) : i18n.t('aiOps:library.noContent')
}

type DocEdit = { type: string; title: string; note: string; content: string | null }

export function AiLibraryPage() {
  const { t } = useTranslation('aiOps')
  const RULES_NOTE = t('library.rulesNote')
  const TEMPLATE_NOTE = t('library.templateNote')
  // Sửa tài liệu / kỹ năng dùng chung: PUT /ai/logic-docs, /ai/scenarios → backend đòi `ai.update`. Không có quyền → chỉ xem.
  const { canEdit } = useAiPerms()
  const qc = useQueryClient()
  const { data, isLoading, isError } = useAiLibrary()
  const { data: botsData } = useAiBots()
  const bots = useMemo(() => (botsData?.bots ?? []).filter((b) => b.kind === 'builtin'), [botsData])
  const [doc, setDoc] = useState<DocEdit | null>(null)
  const [scopeOf, setScopeOf] = useState<ScenarioMeta | null>(null)
  const [skill, setSkill] = useState<ScenarioMeta | 'new' | null>(null)

  const toggle = async (sc: ScenarioMeta, enabled: boolean) => {
    try {
      await updateScenario(sc.id, { enabled })
      await invalidateLibrary(qc)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const docRow = (d: LibraryDoc, note: string) => {
    const label = LIBRARY_DOC_LABEL[d.type as keyof typeof LIBRARY_DOC_LABEL]
    return (
      <li key={d.type}>
        <button
          type="button"
          className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50"
          onClick={() => setDoc({ type: d.type, title: d.title || label?.title || d.type, note, content: d.content })}
        >
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium">{d.title || label?.title || d.type}</span>
            <span className="block truncate text-xs text-muted-foreground">{label?.hint ? `${label.hint} · ` : ''}{preview(d.content)}</span>
          </span>
          <Pencil className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </li>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('library.title')}
        description={t('library.description')}
      />
      {isLoading ? (
        <Loading label={t('library.loading')} />
      ) : isError || !data ? (
        <ErrorState message={t('library.loadError')} />
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> {t('library.rulesTitle')}</CardTitle>
              <CardDescription>{RULES_NOTE}</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="divide-y rounded-lg border">{data.sharedRules.map((d) => docRow(d, RULES_NOTE))}</ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
              <div className="space-y-1.5">
                <CardTitle className="flex items-center gap-2"><Wand2 className="h-4 w-4 text-primary" /> {t('library.skillsTitle')}</CardTitle>
                <CardDescription>
                  {t('library.skillsDesc')}
                </CardDescription>
              </div>
              {canEdit && <Button size="sm" onClick={() => setSkill('new')}><Plus className="h-4 w-4" /> {t('library.addSkill')}</Button>}
            </CardHeader>
            <CardContent>
              {data.scenarios.length === 0 ? (
                <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">{t('library.noSkills')}</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr className="border-b text-left">
                        <th className="px-3 py-2 font-medium">{t('library.cols.skill')}</th>
                        <th className="px-3 py-2 font-medium">{t('library.cols.scope')}</th>
                        <th className="px-3 py-2 font-medium">{t('library.cols.loadMode')}</th>
                        <th className="px-3 py-2 font-medium">{t('library.cols.enabled')}</th>
                        <th className="px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {data.scenarios.map((sc) => {
                        const scope = skillScopeLabel(sc.botIds, bots)
                        const none = Array.isArray(sc.botIds) && !sc.botIds.some((id) => bots.some((b) => b.id === id))
                        return (
                          <tr key={sc.id} className="border-b last:border-b-0 align-top">
                            <td className="max-w-xs px-3 py-2">
                              <p className="font-medium">{sc.name}</p>
                              <p className="line-clamp-2 text-xs text-muted-foreground">{sc.description}</p>
                            </td>
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                className={`max-w-56 truncate rounded px-1.5 py-0.5 text-left underline-offset-2 hover:bg-muted hover:underline ${none ? 'text-warning' : ''}`}
                                onClick={() => setScopeOf(sc)}
                                title={canEdit ? t('library.changeScope') : scope}
                              >
                                {scope}
                              </button>
                            </td>
                            <td className="px-3 py-2">
                              <Badge variant="outline" className="whitespace-nowrap">{sc.loadMode === 'always' ? t('library.loadAlways') : t('library.loadAuto')}</Badge>
                            </td>
                            <td className="px-3 py-2">
                              {canEdit
                                ? <Switch checked={sc.enabled} onCheckedChange={(v) => void toggle(sc, v)} aria-label={t('library.enableSkill', { name: sc.name })} />
                                : <Badge variant={sc.enabled ? 'success' : 'outline'} className="whitespace-nowrap font-normal">{sc.enabled ? t('common:status.enabled') : t('common:status.disabled')}</Badge>}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <Button variant="ghost" size="icon" onClick={() => setSkill(sc)} title={canEdit ? t('library.editSkill') : t('library.viewSkill')} aria-label={canEdit ? t('library.editSkillNamed', { name: sc.name }) : t('library.viewSkillNamed', { name: sc.name })}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><BookCopy className="h-4 w-4 text-primary" /> {t('library.templatesTitle')}</CardTitle>
              <CardDescription>{TEMPLATE_NOTE}</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="divide-y rounded-lg border">
                {TEMPLATE_TYPES.map((tp) => docRow({ type: tp, title: LIBRARY_DOC_LABEL[tp].title, content: data.templates[tp] }, TEMPLATE_NOTE))}
              </ul>
            </CardContent>
          </Card>
        </>
      )}

      {doc && (
        <LibraryDocDialog
          type={doc.type} title={doc.title} note={doc.note} initial={doc.content} canEdit={canEdit}
          onClose={() => setDoc(null)}
        />
      )}
      {scopeOf && <ScopeDialog scenario={scopeOf} bots={bots} canEdit={canEdit} onClose={() => setScopeOf(null)} />}
      {skill && <SkillDialog scenario={skill === 'new' ? null : skill} bots={bots} canEdit={canEdit} onClose={() => setSkill(null)} />}
    </div>
  )
}
